/**
 * App discovery.
 *
 * Adding a UAT app is one directory under `src/apps/` containing an
 * `app.config.ts` that default-exports `defineApp({ ... })`. Nothing else in the
 * repo is edited: the router, the nav drawer and the home index all derive from
 * what this module finds.
 *
 * That is the whole point of the shell. In the repo this app came from, a second
 * standalone app meant seven manual registrations across six files, two of which
 * failed silently — a missing vitest `include` meant the tests never ran, and a
 * missing Dockerfile `COPY` meant the route 404'd at runtime rather than failing
 * the build.
 */
import type { RouteComponent, RouteRecordRaw } from 'vue-router';

export interface UatAppConfig {
    /**
     * Stable identifier. Becomes the route name and namespaces the app's
     * localStorage. Changing it orphans a tester's saved settings, so don't.
     */
    id: string;
    /** Route path without a leading slash: 'sale-intake' serves at /sale-intake. */
    path: string;
    /** Nav label and document title. */
    title: string;
    /** mdi- icon name for the nav list and the home tiles. */
    icon: string;
    /** One line, shown on the home index. */
    description?: string;
    /**
     * The lazy chunk boundary. Always `() => import('./Whatever.vue')`.
     *
     * Declared here rather than derived from the directory name because Vite's
     * dynamic-import-vars cannot span `../` or `/` — a computed path would have
     * to be resolved eagerly, defeating the code splitting.
     */
    component: () => Promise<RouteComponent | { default: RouteComponent }>;
    /**
     * Tenant ids that may see and open this app. Omit for "every tenant".
     *
     * Most UAT apps are written against one customer's graph — a fixture label, a
     * field map, a client id — and are meaningless anywhere else. Gating keeps the
     * index honest rather than offering a tester an app that can only fail.
     *
     * Enforced in three places that must agree: the nav drawer, the home index and
     * the route guard. The guard is the one that matters, because the other two
     * only hide a link — a bookmarked deep link would otherwise still open the app
     * against the wrong tenant's graph. Use {@link TENANT_IDS} rather than a
     * literal uuid.
     */
    tenants?: string[];
    /** Default true. Set false only for something that genuinely needs no token. */
    requiresAuth?: boolean;
    /** Nav sort order, ascending. Default 100. */
    order?: number;
    /** Routable but not listed in the nav or on the home index. */
    hidden?: boolean;
}

export interface AppEntry extends UatAppConfig {
    /** Absolute route path, e.g. '/sale-intake'. */
    routePath: string;
}

/** Identity function. Exists purely so an app config gets type-checked at its definition. */
export function defineApp(config: UatAppConfig): UatAppConfig {
    return config;
}

type GlobRecord = Record<string, { default: UatAppConfig }>;

/**
 * Validates and normalises what the glob found.
 *
 * Pure, and takes the record as an argument, so it unit-tests without Vite.
 *
 * Throws rather than skipping a malformed config: a typo in a new app's `path`
 * should stop the shell at boot with the offending filename, not silently
 * produce a route that 404s when someone finally clicks it.
 */
export function toEntries(modules: GlobRecord): AppEntry[] {
    const entries = Object.entries(modules).map(([file, mod]) => {
        const config = mod?.default;

        if (!config?.id || !config.path || !config.component) {
            throw new Error(
                `${file}: app.config.ts must default-export defineApp({ id, path, title, icon, component })`,
            );
        }
        if (config.path.startsWith('/')) {
            throw new Error(`${file}: path must not start with '/' (got '${config.path}')`);
        }

        return { ...config, routePath: `/${config.path}` };
    });

    for (const key of ['id', 'routePath'] as const) {
        const seen = new Map<string, string>();
        for (const entry of entries) {
            const previous = seen.get(entry[key]);
            if (previous) {
                throw new Error(
                    `Duplicate app ${key} '${entry[key]}': declared by both '${previous}' and '${entry.id}'`,
                );
            }
            seen.set(entry[key], entry.id);
        }
    }

    return entries.sort(
        (a, b) => (a.order ?? 100) - (b.order ?? 100) || a.title.localeCompare(b.title),
    );
}

/**
 * The entries a given tenant may use.
 *
 * Pure and exported so the nav, the home index and the guard all ask the same
 * question of the same function — three copies of this predicate would be three
 * chances to disagree about who can see what.
 *
 * A null tenant (none confirmed yet) yields only ungated apps. That is the safe
 * direction: the shell never shows a tenant-specific app before it knows which
 * tenant it is talking to.
 */
export function entriesForTenant(entries: AppEntry[], tenantId: string | null): AppEntry[] {
    return entries.filter(
        (entry) => !entry.tenants || (tenantId !== null && entry.tenants.includes(tenantId)),
    );
}

/** Route records for the discovered apps. `component` is passed through uninvoked. */
export function toRoutes(entries: AppEntry[]): RouteRecordRaw[] {
    return entries.map((entry) => ({
        path: entry.routePath,
        name: entry.id,
        component: entry.component,
        meta: {
            title: entry.title,
            icon: entry.icon,
            requiresAuth: entry.requiresAuth ?? true,
            appId: entry.id,
            tenants: entry.tenants,
        },
    }));
}

// The one Vite-specific line in the shell. Eager because both the router and the
// nav need the configs synchronously at boot; the components they point at stay
// lazy, so Rollup still emits a chunk per app.
export const appEntries: AppEntry[] = toEntries(
    import.meta.glob<{ default: UatAppConfig }>('../apps/*/app.config.ts', { eager: true }),
);

/** What the nav drawer and home index render. Derived, never hand-maintained. */
export const navEntries: AppEntry[] = appEntries.filter((entry) => !entry.hidden);
